import Button from '@liquidweb-dev/undercurrent/Button';
import Stack, { type StackProps } from '@liquidweb-dev/undercurrent/Stack';
import CreditCardIcon from '@liquidweb-dev/undercurrent/icons/lucide/CreditCard';
import PayPalLogo from '@liquidweb-dev/undercurrent/logos/PayPal';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import useBillingPaymentProfileDetails from 'modules/queries/billing/payment/profile/useDetails';
import { useOpenPayPalPaymentApprovalUrl } from 'modules/queries/billing/paypal/payment/useCreate';
import useBrandName from 'utility/hooks/useBrandName';
import { useDialogMakePaymentContext } from '../dialogMakePaymentContext';

const ChoosePaymentMethodContent = ({ sx, ...rest }: StackProps) => {
	const brandName = useBrandName();
	const { formValues, setStep } = useDialogMakePaymentContext();
	const { amount } = formValues;

	const {
		openPayPalPaymentApprovalUrl,
		isLoading: isOpenPayPalPaymentApprovalUrlLoading,
	} = useOpenPayPalPaymentApprovalUrl();

	const { data: paymentProfileDetails } = useBillingPaymentProfileDetails({});
	const { payment_method: paymentMethod } = paymentProfileDetails || {};
	// Can't choose one-time PayPal payment method if billing profile is already
	// set to PayPal
	const showPayPalButton = paymentMethod !== 'paypal';
	// Can't go back to start if billing profile is set to check (can't make a
	// one-time check payment through the interface, obviously)
	const showBackButton = paymentMethod !== 'check';

	const continueDisabled = Number(formValues.amount) <= 0;

	return (
		<Stack sx={combineSx({ gap: 200 }, sx)} {...rest}>
			{showPayPalButton && (
				<Button
					variant="secondary"
					size="large"
					sx={{
						backgroundColor: '#FFC439',
						'&:hover': {
							backgroundColor: '#FFC43980',
						},
					}}
					aria-label="Use PayPal"
					disabled={continueDisabled}
					loading={isOpenPayPalPaymentApprovalUrlLoading}
					onClick={() =>
						// leaves my.lw and opens PayPal site...
						openPayPalPaymentApprovalUrl({
							requested_amount: amount,
							lineItems: [{ amount, description: `My ${brandName} Payment` }],
							// ...Payment flow continues at returnURL's path
							returnURL: `${window.location.origin}/account/billing/confirm-one-time-paypal-payment`,
						})
					}
				>
					<Stack sx={{ flexDirection: 'row', gap: 100 }}>
						<span>Use</span>
						<PayPalLogo sx={{ height: '16px', width: '60px' }} />
					</Stack>
				</Button>
			)}

			<Button
				variant="primary"
				size="large"
				startIcon={<CreditCardIcon />}
				onClick={() => setStep('useNewCard')}
				disabled={isOpenPayPalPaymentApprovalUrlLoading || continueDisabled}
			>
				New card
			</Button>

			{showBackButton && (
				<Button
					variant="secondary"
					size="large"
					onClick={() => setStep('start')}
					disabled={isOpenPayPalPaymentApprovalUrlLoading}
				>
					Go back
				</Button>
			)}
		</Stack>
	);
};

export default ChoosePaymentMethodContent;
